import * as React from 'react';
const { useState, useRef, useEffect, useCallback } = React;
import { useStore } from '@nanostores/react';
import { useAssistantChat } from '../hooks/useAssistantChat';
import { assistantStreamBuffer, assistantStreaming, assistantSuggestions, assistantSelectInput } from '../stores/assistantStore';
import { AssistantMessage, renderMarkdown } from './AssistantMessage';
import { AssistantQuickChips } from './AssistantQuickChips';
import { AssistantSelectInput } from './AssistantSelectInput';
import type { QuickChip, SelectInputOption } from '../types/assistant';

interface AssistantChatProps {
  userName: string;
}

export const AssistantChat: React.FC<AssistantChatProps> = ({ userName }) => {
  const { messages, sendMessage, cancelStream } = useAssistantChat();
  const streaming = useStore(assistantStreaming);
  const streamBuffer = useStore(assistantStreamBuffer);
  const suggestions = useStore(assistantSuggestions);
  const selectInput = useStore(assistantSelectInput);
  const [input, setInput] = useState('');

  const initialChips: QuickChip[] = [
    { text: 'Post a Job', icon: 'fa-plus', message: 'I want to post a new job' },
    { text: 'Search Candidates', icon: 'fa-search', message: 'Search for candidates' },
    { text: 'View Applicants', icon: 'fa-users', message: 'Show me my applicants' },
    { text: 'Manage Jobs', icon: 'fa-briefcase', message: 'Show me my active jobs' },
  ];

  const handleChipClick = useCallback((chip: QuickChip) => {
    if (streaming) return;
    sendMessage(chip.message);
  }, [streaming, sendMessage]);

  const handleSelectOption = useCallback((opt: SelectInputOption) => {
    if (streaming) return;
    sendMessage(opt.message);
  }, [streaming, sendMessage]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamBuffer]);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || streaming) return;
    setInput('');
    sendMessage(trimmed);
  }, [input, streaming, sendMessage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  };

  const messagesStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 16px',
  };

  const inputAreaStyle: React.CSSProperties = {
    borderTop: '1px solid #e2e8f0',
    padding: '8px 12px',
    display: 'flex',
    gap: 8,
    alignItems: 'flex-end',
  };

  return (
    <div style={containerStyle}>
      <div style={messagesStyle}>
        {messages.length === 0 && !streaming && (
          <div style={{ textAlign: 'center', color: '#a0aec0', padding: '40px 20px' }}>
            <i className="fas fa-robot" style={{ fontSize: 32, marginBottom: 12, display: 'block' }} />
            <p style={{ fontWeight: 600, marginBottom: 4 }}>Hi {userName}!</p>
            <p style={{ fontSize: 13 }}>
              I can help you manage jobs, find candidates, and track applicants. What would you like to do?
            </p>
            <div style={{ marginTop: 12 }}>
              <AssistantQuickChips chips={initialChips} onChipClick={handleChipClick} />
            </div>
          </div>
        )}
        {messages.map((msg) => (
          <AssistantMessage key={msg.id} message={msg} />
        ))}
        {streaming && streamBuffer && (
          <div style={{ padding: '8px 12px', background: '#f7fafc', borderRadius: 8, marginBottom: 8 }}>
            {/* Content is sanitized via DOMPurify inside renderMarkdown */}
            <div
              className="didi-markdown"
              style={{ fontSize: 13 }}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(streamBuffer) }}
            />
          </div>
        )}
        {streaming && !streamBuffer && (
          <div style={{ padding: '8px 12px', color: '#a0aec0', fontSize: 13 }}>
            <i className="fas fa-circle-notch fa-spin" style={{ marginRight: 6 }} />
            Thinking...
          </div>
        )}
        {!streaming && selectInput && (
          <div style={{ padding: '4px 12px' }}>
            <AssistantSelectInput payload={selectInput} onSelect={handleSelectOption} />
          </div>
        )}
        {!streaming && !selectInput && suggestions.length > 0 && (
          <div style={{ padding: '4px 12px' }}>
            <AssistantQuickChips chips={suggestions} onChipClick={handleChipClick} />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div style={inputAreaStyle} data-tour="didi-composer">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about jobs, candidates, or applicants..."
          rows={1}
          style={{
            flex: 1,
            resize: 'none',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 13,
            fontFamily: 'inherit',
            outline: 'none',
            maxHeight: 100,
            overflow: 'auto',
          }}
          disabled={streaming}
        />
        {streaming ? (
          <button
            onClick={cancelStream}
            style={{
              background: '#e53e3e',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            <i className="fas fa-stop" />
          </button>
        ) : (
          <button
            onClick={handleSend}
            disabled={!input.trim()}
            style={{
              background: input.trim() ? '#2C5282' : '#cbd5e0',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 12px',
              cursor: input.trim() ? 'pointer' : 'default',
              fontSize: 13,
            }}
          >
            <i className="fas fa-paper-plane" />
          </button>
        )}
      </div>
    </div>
  );
};
